import React, {Component, useEffect, useMemo, useState} from "react";
import {Link} from "react-router-dom";
import {AgGridReact} from "ag-grid-react";
import {
    AllCommunityModule,
    ClientSideRowModelModule,
    ColumnMenuModule,
    ColumnsToolPanelModule,
    ContextMenuModule,
    CsvExportModule,
    ExcelExportModule,
    SetFilterModule,
    MultiFilterModule,
} from "ag-grid-enterprise";
import {AgChartsReact} from "ag-charts-react";
import {Helmet} from "react-helmet";
import {defaultZenGroupColumnInitWithOptionsWithValueGetter} from "../../utils/zenGroupDisplayNameGridHelper";
import {findZenGroupById} from "../../utils/zenGroupSessionStorageManager";
import {
    defaultAgentNameColumnAndMachineNameInitWithFilters,
    defaultMachineNameColDefWithFilters
} from "../../utils/agentNameGridHelper";
import {
    getChartVisibilitySettingInSession,
    getColumnModeInSession,
    getDefaultAgGridSidebarProps,
    getUseColumnStateInSession,
    getUseFilterStateInSession,
    onColumnStateChangedHelper,
    onFilterChangedHelper,
    onGridReadyHelper,
    onGridReadyHelperForColumnState,
    updateChartVisibilitySettingInSession,
    updateColumnModeInSessionHelper,
    updateUseColumnStateHelper,
    updateUseFilterStateHelper
} from "../../utils/gridFilterStateAndColumnStateHelper";
import {ClearRefresh} from "../../components/clearRefreshButtons";
import privatePageHeaderHelper from "../../utils/privatePageHeaderHelper";
import {BackDropPageLoadingOverlay} from "../../components/BackDropComponents";
import {
    ClickToShowColumnOptionsWithToggleButtonGroup,
    customColumnModeText,
    minColumnModeText,
    standardApplyMinimumOrMediumColumnMode
} from "../../components/clickToShowButtons";
import {DisplayOptions} from "../../components/displayOptionsComponent";
import DTPicker, {
    dateFilterParametersInHeaderDayOnly,
    dateFilterParametersInHeaderSuppressSorting,
    dateFilterParametersInHeaderClientSideGrid
} from "../../utils/DTPicker";
import {dateValueFormatter} from "../../utils/gridDateFormatter";
import CustomNameCellEditor from "../../utils/customCellEditor";
import {handleGroupColumnChangeNameOnly} from "../../utils/gridCellEditing";
import {
    updateFortressAiReportMetricsGridUseFilterStateReactive,
    updateFortressAiReportMetricsGridFilterModelReactive,
    updateFortressAiReportMetricsGridUseColumnStateReactive,
    updateFortressAiReportMetricsGridColumnStateReactive,
    updateFortressAiReportMetricsGridColumnModeReactive,
    updateFortressAiReportMetricsShowHourlyReactive,
    updateFortressAiReportMetricsChartVisibilityReactive,
    updateFortressAiReportMetricsHourlyGridFilterModelReactive,
    updateFortressAiReportMetricsHourlyGridUseFilterStateReactive,
    updateFortressAiReportMetricsHourlyGridColumnStateReactive,
    updateFortressAiReportMetricsHourlyGridUseColumnStateReactive,
    updateFortressAiReportMetricsHourlyGridColumnModeReactive,
} from "../api/fortressAiReportApi";
import {changeAgentNameReactive} from "../api/agentsApi";
import NotificationManager from "react-notifications/lib/NotificationManager";
import {defaultClientSideTextFilterParams} from "../../utils/filterHelper";
import {
    loadDataWithSSEAndStartChangeStreamListener,
    standardHandleInsertEvent,
    standardHandlePopulateGrid,
    standardHandleUpdateAndReplaceEvent
} from "../../utils/sseAndChangeStreamHelper";
import {decryptAndGetSessionVariable, encryptAndStoreSessionVariable} from "../../utils/storageHelper";
import {standardExcelExportHelper} from "../../utils/excelExportHelper";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {MuiIconButtonWithTooltip} from "../../components/muiComponents";
import {Tooltip} from "@mui/material";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

// ─── Session variable names ───────────────────────────────────────────────────

const dailyGridColumnStateSessionVariableName  = "fortressAiReportMetricsGridColumnState"
const dailyGridFilterStateSessionVariableName  = "fortressAiReportMetricsGridFilterState"
const hourlyGridColumnStateSessionVariableName = "fortressAiReportMetricsHourlyGridColumnState"
const hourlyGridFilterStateSessionVariableName = "fortressAiReportMetricsHourlyGridFilterState"
const showHourlySessionVariableName            = "fortressAiReportMetricsShowHourly"
const chartVisibleSessionVariableName          = "fortressAiReportMetricsChartVisible"

// ─── Column id sets ───────────────────────────────────────────────────────────

const dailyMinColumnIds  = ["count", "dateDayRounded",  "aiTool", "username"]
const hourlyMinColumnIds = ["count", "dateHourRounded", "aiTool", "username"]

// ─── Chart groupBy constants ──────────────────────────────────────────────────

const GROUP_BY_AI_TOOL  = "aiTool"
const GROUP_BY_GROUP    = "group"
const GROUP_BY_USERNAME = "username"
const GROUP_BY_DATE     = "date"

// ─── AI tool display label map ────────────────────────────────────────────────

const AI_TOOL_LABEL = {
    CHAT_GPT: "ChatGPT",
    CLAUDE:   "Claude",
    COPILOT:  "Copilot",
    GENSPARK: "Genspark",
    COMET:    "Perplexity",
    MAXATHON: "Maxathon",
    OTHER:    "Other",
}

// Palette for line chart series (one per AI tool)
const SERIES_COLORS = [
    "#e76a24", "#2563eb", "#16a34a", "#9333ea",
    "#dc2626", "#0891b2", "#ca8a04", "#be185d",
]

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getShowHourlyFromSession() {
    try {
        const val = decryptAndGetSessionVariable(showHourlySessionVariableName)
        return val ? JSON.parse(val) : false
    } catch {
        return false
    }
}

function aiToolLabel(rawValue) {
    return AI_TOOL_LABEL[rawValue] ?? rawValue ?? "Other"
}

function aiToolColumnDef() {
    return {
        field: "aiTool",
        headerName: "AI Tool",
        initialWidth: 180,
        sortable: true,
        filter: "agSetColumnFilter",
        filterParams: {
            buttons: ["reset", "apply", "cancel"],
            values: Object.keys(AI_TOOL_LABEL),
            valueFormatter: (p) => aiToolLabel(p.value),
        },
        valueFormatter: (p) => aiToolLabel(p.value),
    }
}

function usernameColumnDef() {
    return {
        field: "username",
        headerName: "Username",
        initialWidth: 300,
        sortable: true,
        filter: "agTextColumnFilter",
        filterParams: defaultClientSideTextFilterParams,
        cellRenderer: (params) => params.value ?? "",
    }
}

function drillDownCellRenderer(params, dateField) {
    const {aiTool, username, zenGroupId} = params.data || {}
    const rawDate  = params.data?.[dateField]
    const isHourly = dateField === "dateHourRounded"

    const dateObj = new Date(rawDate)

    const linkState = {
        drilldownAiTool:   aiTool,
        drilldownUsername: username,
        drilldownEpochMs:  dateObj.getTime(),
        drilldownIsHourly: isHourly,
        drilldownZenGroupId: zenGroupId,
    }

    return (
        <div className="flex items-center gap-x-1">
            <Link to="/private/fortressAiReport" state={linkState}>
                <MuiIconButtonWithTooltip
                    icon={<FontAwesomeIcon className="object-contain" icon="fa-duotone fa-binoculars" size="xs"/>}
                    tooltipTitle={"Click to view individual reports for this metric"}
                    tooltipPlacement={"bottom-start"}
                />
            </Link>
            {params.value}
        </div>
    )
}

// ─── Chart options builder ────────────────────────────────────────────────────
// Returns an AgCharts options object.
// - Categorical groupBy (aiTool, group, username) → bar chart
// - Date groupBy → line chart, one series per AI tool

function buildChartOptions(rowData, groupBy, showHourly) {
    if (!rowData || rowData.length === 0) return null

    const titleMap = {
        [GROUP_BY_AI_TOOL]:  "AI Report Usage by AI Tool",
        [GROUP_BY_GROUP]:    "AI Report Usage by Group",
        [GROUP_BY_USERNAME]: "AI Report Usage by Username",
        [GROUP_BY_DATE]:     showHourly ? "AI Report Usage by Hour" : "AI Report Usage by Date",
    }

    if (groupBy === GROUP_BY_DATE) {
        // ── Line chart ────────────────────────────────────────────────────────
        const dateToolMap = new Map() // label → { tool → count, epoch }
        const toolSet     = new Set()

        rowData.forEach(row => {
            const raw = showHourly ? row.dateHourRounded : row.dateDayRounded
            if (!raw) return
            const d     = new Date(raw)
            const label = showHourly
                ? d.toLocaleString("en-US", {
                    month: "short", day: "numeric",
                    hour: "numeric", minute: "2-digit",
                    timeZone: "UTC",
                })
                : d.toLocaleDateString("en-US", {
                    month: "short", day: "numeric", year: "numeric",
                    timeZone: "UTC",
                })
            const tool = aiToolLabel(row.aiTool)
            toolSet.add(tool)
            if (!dateToolMap.has(label)) dateToolMap.set(label, {label, epoch: d.getTime()})
            const entry = dateToolMap.get(label)
            entry[tool] = (entry[tool] || 0) + (row.count || 0)
        })

        const sorted = [...dateToolMap.values()].sort((a, b) => a.epoch - b.epoch)
        sorted.forEach(d => { delete d.epoch })

        const tools   = [...toolSet].sort()
        const series  = tools.map((tool, i) => ({
            type:   "line",
            xKey:   "label",
            yKey:   tool,
            yName:  tool,
            stroke: SERIES_COLORS[i % SERIES_COLORS.length],
            marker: {
                enabled: sorted.length <= 30,
                fill:    SERIES_COLORS[i % SERIES_COLORS.length],
            },
            tooltip: {
                renderer: (p) => ({
                    title:   p.yName,
                    content: `Count: ${p.datum[p.yKey]}`,
                })
            },
        }))

        return {
            autoSize: true,
            data:     sorted,
            title:    {text: titleMap[groupBy], fontSize: 16},
            series,
            axes: [
                {
                    type:     "category",
                    position: "bottom",
                    label:    {rotation: sorted.length > 8 ? 45 : 0, fontSize: 10},
                },
                {
                    type:     "number",
                    position: "left",
                    title:    {text: "Total Reports"},
                },
            ],
            legend: {position: "top", enabled: tools.length > 1},
        }
    }

    // ── Bar chart (categorical) ───────────────────────────────────────────────
    const countMap = new Map()
    rowData.forEach(row => {
        let label
        if (groupBy === GROUP_BY_AI_TOOL)  label = aiToolLabel(row.aiTool)
        if (groupBy === GROUP_BY_GROUP)    label = row.zenGroupDisplayName || row.zenGroupId || "Unknown"
        if (groupBy === GROUP_BY_USERNAME) label = row.username || "(no username)"
        if (!label) return
        countMap.set(label, (countMap.get(label) || 0) + (row.count || 0))
    })

    const data = [...countMap.entries()]
        .sort((a, b) => b[1] - a[1])
        .map(([label, count], i) => ({label, count, color: SERIES_COLORS[i % SERIES_COLORS.length]}))

    return {
        autoSize: true,
        data,
        title:  {text: titleMap[groupBy], fontSize: 16},
        series: [
            {
                type:    "bar",
                xKey:    "label",
                yKey:    "count",
                yName:   "Total Reports",
                fills:   data.map(d => d.color),
                strokes: data.map(() => "#c2c2c2"),
                strokeWidth: 1,
                highlightStyle: {
                    item: {fill: "#E8E8E8", stroke: "#181818", strokeWidth: 1},
                },
                label: {
                    enabled:   data.length <= 20,
                    formatter: (p) => String(p.value),
                },
                tooltip: {
                    renderer: (p) => ({
                        title:   p.datum.label,
                        content: `Count: ${p.datum.count}`,
                    })
                },
            },
        ],
        axes: [
            {
                type:     "category",
                position: "bottom",
                label:    {rotation: data.length > 8 ? 45 : 0, fontSize: 10},
            },
            {
                type:     "number",
                position: "left",
                title:    {text: "Total Reports"},
            },
        ],
        legend: {enabled: false},
    }
}

// ─── Page component ───────────────────────────────────────────────────────────

export default function FortressAiReportMetrics() {
    const [isLoading, setIsLoading] = useState(false)
    const [gridApi,   setGridApi]   = useState(null)

    const [showHourly,   setShowHourly]   = useState(getShowHourlyFromSession)
    const [chartToggled, setChartToggled] = useState(getChartVisibilitySettingInSession(chartVisibleSessionVariableName))
    const [chartGroupBy, setChartGroupBy] = useState(GROUP_BY_AI_TOOL)

    const [chartGroupId, setChartGroupId] = useState(null)

    // Raw cache from Grid — { daily: [], hourly: [] }
    const [rawChartData, setRawChartData] = useState({daily: [], hourly: []})
    const [chartOptions, setChartOptions] = useState({})

    // Derive group dropdown options from whichever rows are active — only groups present in the data
    const chartGroupOptions = useMemo(() => {
        const activeRows = showHourly ? rawChartData.hourly : rawChartData.daily
        const seen = new Map()
        activeRows.forEach(r => {
            if (r.zenGroupId && !seen.has(r.zenGroupId)) {
                const group = findZenGroupById(r.zenGroupId)
                seen.set(r.zenGroupId, group?.friendlyName || r.zenGroupDisplayName || r.zenGroupId)
            }
        })
        return [...seen.entries()]
            .map(([id, label]) => ({id, label}))
            .sort((a, b) => a.label.localeCompare(b.label))
    }, [rawChartData, showHourly])

    // When options change, ensure selected group is still valid; default to first if not
    useEffect(() => {
        if (chartGroupOptions.length === 0) return
        const stillValid = chartGroupOptions.some(g => g.id === chartGroupId)
        if (!stillValid) setChartGroupId(chartGroupOptions[0].id)
    }, [chartGroupOptions])

    // Recompute chart options whenever source data, groupBy, group filter, or toggle changes
    useEffect(() => {
        const activeRows = showHourly ? rawChartData.hourly : rawChartData.daily
        // For AI Tool / Username / Date modes, filter to the selected group only
        const filteredRows = chartGroupBy !== GROUP_BY_GROUP && chartGroupId
            ? activeRows.filter(r => r.zenGroupId === chartGroupId)
            : activeRows
        const opts = buildChartOptions(filteredRows, chartGroupBy, showHourly)
        setChartOptions(opts || {})
    }, [rawChartData, chartGroupBy, chartGroupId, showHourly])

    // ── Daily grid state ──────────────────────────────────────────────────────
    const [dailyUseFilterState,  setDailyUseFilterState]  = useState(getUseFilterStateInSession(dailyGridFilterStateSessionVariableName))
    const [dailyUseColumnState,  setDailyUseColumnState]  = useState(getUseColumnStateInSession(dailyGridColumnStateSessionVariableName))
    const [dailyColumnMode,      setDailyColumnMode]      = useState(getColumnModeInSession(dailyGridColumnStateSessionVariableName))

    // ── Hourly grid state ─────────────────────────────────────────────────────
    const [hourlyUseFilterState, setHourlyUseFilterState] = useState(getUseFilterStateInSession(hourlyGridFilterStateSessionVariableName))
    const [hourlyUseColumnState, setHourlyUseColumnState] = useState(getUseColumnStateInSession(hourlyGridColumnStateSessionVariableName))
    const [hourlyColumnMode,     setHourlyColumnMode]     = useState(getColumnModeInSession(hourlyGridColumnStateSessionVariableName))

    const [sseDataPullActive,          setSSEDataPullActive]          = useState(true)
    const [asyncTransactionWaitMillis, setAsyncTransactionWaitMillis] = useState(200)

    // Derive active props from toggle
    const activeColumnMode                   = showHourly ? hourlyColumnMode         : dailyColumnMode
    const setActiveColumnMode                = showHourly ? setHourlyColumnMode      : setDailyColumnMode
    const activeUseFilterState               = showHourly ? hourlyUseFilterState     : dailyUseFilterState
    const setActiveUseFilterState            = showHourly ? setHourlyUseFilterState  : setDailyUseFilterState
    const activeUseColumnState               = showHourly ? hourlyUseColumnState     : dailyUseColumnState
    const setActiveUseColumnState            = showHourly ? setHourlyUseColumnState  : setDailyUseColumnState
    const activeGridColumnStateSessionVar    = showHourly ? hourlyGridColumnStateSessionVariableName : dailyGridColumnStateSessionVariableName
    const activeGridFilterStateSessionVar    = showHourly ? hourlyGridFilterStateSessionVariableName : dailyGridFilterStateSessionVariableName
    const activeMinColumnIds                 = showHourly ? hourlyMinColumnIds       : dailyMinColumnIds
    const activeUpdateColumnModeReactive     = showHourly ? updateFortressAiReportMetricsHourlyGridColumnModeReactive    : updateFortressAiReportMetricsGridColumnModeReactive
    const activeUpdateFilterModelReactive    = showHourly ? updateFortressAiReportMetricsHourlyGridFilterModelReactive   : updateFortressAiReportMetricsGridFilterModelReactive
    const activeUpdateUseFilterStateReactive = showHourly ? updateFortressAiReportMetricsHourlyGridUseFilterStateReactive : updateFortressAiReportMetricsGridUseFilterStateReactive
    const activeUpdateColumnStateReactive    = showHourly ? updateFortressAiReportMetricsHourlyGridColumnStateReactive   : updateFortressAiReportMetricsGridColumnStateReactive
    const activeUpdateUseColumnStateReactive = showHourly ? updateFortressAiReportMetricsHourlyGridUseColumnStateReactive : updateFortressAiReportMetricsGridUseColumnStateReactive

    function toggleUpdateUseFilterState(toggleSetting) {
        updateUseFilterStateHelper(toggleSetting, activeGridFilterStateSessionVar, activeUpdateUseFilterStateReactive)
    }

    function toggleUpdateUseColumnState(toggleSetting) {
        updateUseColumnStateHelper(toggleSetting, activeGridColumnStateSessionVar, activeUpdateUseColumnStateReactive)
    }

    function handleShowHourlyToggle(newValue) {
        setShowHourly(newValue)
        encryptAndStoreSessionVariable(showHourlySessionVariableName, JSON.stringify(newValue))
        updateFortressAiReportMetricsShowHourlyReactive(newValue).catch(err => {
            console.error("Failed to persist hourly/daily toggle", err)
        })
    }

    function excelExport() {
        standardExcelExportHelper(
            gridApi,
            sseDataPullActive,
            showHourly ? "fortressAiReportMetricsHourlyGridExport" : "fortressAiReportMetricsGridExport"
        )
    }

    async function refreshGrid() {}

    // ── Column defs ───────────────────────────────────────────────────────────

    // eslint-disable-next-line no-unused-vars
    const [dailyColumnDefs] = useState([
        {
            field: "count",
            headerName: "Total Reports",
            initialWidth: 220,
            sortable: true,
            pinned: "left",
            filter: "agNumberColumnFilter",
            filterParams: {
                suppressSorting: true,
                buttons: ["reset", "apply"],
                filterOptions: ["equals", "greaterThan", "lessThan", "greaterThanOrEqual", "lessThanOrEqual"],
                maxNumConditions: 2,
            },
            cellRenderer: (params) => drillDownCellRenderer(params, "dateDayRounded"),
        },
        defaultZenGroupColumnInitWithOptionsWithValueGetter(true, true, true),
        defaultAgentNameColumnAndMachineNameInitWithFilters(5),
        defaultMachineNameColDefWithFilters(5),
        aiToolColumnDef(),
        usernameColumnDef(),
        {
            field: "dateDayRounded",
            headerName: "Day Start",
            initialWidth: 220,
            sortable: true,
            filter: "agDateColumnFilter",
            filterParams: dateFilterParametersInHeaderSuppressSorting(2),
            valueFormatter: dateValueFormatter,
        },
    ])

    // eslint-disable-next-line no-unused-vars
    const [hourlyColumnDefs] = useState([
        {
            field: "count",
            headerName: "Total Reports",
            initialWidth: 220,
            sortable: true,
            pinned: "left",
            filter: "agNumberColumnFilter",
            filterParams: {
                suppressSorting: true,
                buttons: ["reset", "apply"],
                filterOptions: ["equals", "greaterThan", "lessThan", "greaterThanOrEqual", "lessThanOrEqual"],
                maxNumConditions: 2,
            },
            cellRenderer: (params) => drillDownCellRenderer(params, "dateHourRounded"),
        },
        defaultZenGroupColumnInitWithOptionsWithValueGetter(true, true, true),
        defaultAgentNameColumnAndMachineNameInitWithFilters(5),
        defaultMachineNameColDefWithFilters(5),
        aiToolColumnDef(),
        usernameColumnDef(),
        {
            field: "dateHourRounded",
            headerName: "Hour",
            initialWidth: 260,
            sortable: true,
            filter: "agDateColumnFilter",
            filterParams: dateFilterParametersInHeaderSuppressSorting(2),
            valueFormatter: dateValueFormatter,
        },
    ])

    const [defaultColDef] = useState({
        resizable: true,
        filterParams: null,
        floatingFilter: true,
        headerClass: "border-0 border-b-0",
        cellClass: "outline:none",
        enableCellChangeFlash: true,
        autoHeight: false,
        cellDataType: false,
    })

    const sideBar      = useMemo(() => getDefaultAgGridSidebarProps(325), [])
    const rowSelection = useMemo(() => ({
        mode: "singleRow",
        enableClickSelection: true,
        checkboxes: false,
        headerCheckbox: false,
    }), [])

    const hasChartData = (showHourly ? rawChartData.hourly : rawChartData.daily).length > 0

    return (
        <div className="flex flex-col h-full">
            <Helmet>
                <meta charSet="utf-8" />
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <title>{showHourly ? "Metrics Hourly" : "Metrics Daily"}</title>
                <script src="https://js.stripe.com/v3/" />
                <link
                    href="https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300;0,400;0,600;0,700;0,800;1,300;1,400;1,600;1,700;1,800&display=swap"
                    rel="stylesheet"
                />
            </Helmet>
            <div className="flex flex-1 flex-col flex-nowrap gap-y-3 h-full">
                {privatePageHeaderHelper(
                    showHourly
                        ? "Fortress AI Report Metrics Hourly"
                        : "Fortress AI Report Metrics Daily"
                )}
                <hr className="h-0.5" />

                <DisplayOptions
                    useFilterStateSettingToggled={activeUseFilterState}
                    setUseFilterStateSettingToggled={setActiveUseFilterState}
                    toggleUpdateUseFilterState={toggleUpdateUseFilterState}
                    useColumnStateSettingToggled={activeUseColumnState}
                    setUseColumnStateSettingToggled={setActiveUseColumnState}
                    toggleUpdateUseColumnState={toggleUpdateUseColumnState}
                    chartToggled={chartToggled}
                    setChartToggled={setChartToggled}
                    chartSessionVariableName={chartVisibleSessionVariableName}
                    updateChartVisibilitySettingInSession={updateChartVisibilitySettingInSession}
                    updateChartVisibilityReactive={updateFortressAiReportMetricsChartVisibilityReactive}
                    chartName={"AI Report Metrics"}
                    gridViewToggled={showHourly}
                    setGridViewToggled={handleShowHourlyToggle}
                    gridViewToggleLabel={"Hourly View"}
                    gridViewToggleOffLabel={"Daily View"}
                />

                {/* ── Chart panel ── */}
                <div className={`chartContainer ${chartToggled ? "block mb-10" : "hidden"} relative`}>
                    {/* GroupBy dimension selector */}
                    <div className="flex flex-row flex-wrap gap-x-4 gap-y-2 items-center justify-start mb-4">
                        <ToggleButtonGroup
                            value={chartGroupBy}
                            exclusive
                            size={"small"}
                            onChange={(_e, val) => { if (val) setChartGroupBy(val) }}
                        >
                            <ToggleButton value={GROUP_BY_AI_TOOL}>
                                <Tooltip arrow enterDelay={750}
                                         title={<div className={"text-sm"}>Bar chart — total reports per AI tool</div>}
                                         placement={"top"}>
                                    <div className={"normal-case"}>AI Tool</div>
                                </Tooltip>
                            </ToggleButton>
                            <ToggleButton value={GROUP_BY_GROUP}>
                                <Tooltip arrow enterDelay={750}
                                         title={<div className={"text-sm"}>Bar chart — total reports per group</div>}
                                         placement={"top"}>
                                    <div className={"normal-case"}>Group</div>
                                </Tooltip>
                            </ToggleButton>
                            <ToggleButton value={GROUP_BY_USERNAME}>
                                <Tooltip arrow enterDelay={750}
                                         title={<div className={"text-sm"}>Bar chart — total reports per username</div>}
                                         placement={"top"}>
                                    <div className={"normal-case"}>Username</div>
                                </Tooltip>
                            </ToggleButton>
                            <ToggleButton value={GROUP_BY_DATE}>
                                <Tooltip arrow enterDelay={750}
                                         title={<div className={"text-sm"}>Line chart — usage trend over time, one line per AI tool</div>}
                                         placement={"top"}>
                                    <div className={"normal-case"}>{showHourly ? "Hour" : "Date"}</div>
                                </Tooltip>
                            </ToggleButton>
                        </ToggleButtonGroup>

                        {/* Group filter — hidden in GROUP_BY_GROUP mode (which already aggregates all groups) */}
                        {chartGroupBy !== GROUP_BY_GROUP && (
                            <Autocomplete
                                size="small"
                                options={chartGroupOptions}
                                value={chartGroupOptions.find(g => g.id === chartGroupId) ?? null}
                                onChange={(_e, option) => {
                                    if (option) setChartGroupId(option.id)
                                }}
                                disableClearable
                                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                                sx={{width: 260}}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Group"
                                        variant="outlined"
                                        size="small"
                                    />
                                )}
                            />
                        )}
                    </div>

                    {hasChartData
                        ? <AgChartsReact options={chartOptions} />
                        : (
                            <div className="flex items-center justify-center h-32 text-slate-400 text-sm">
                                No data available for chart
                            </div>
                        )
                    }
                </div>

                {/* ── Toolbar ── */}
                <div className={"flex flex-row flex-wrap gap-y-3 gap-x-8 self-end justify-end"}>
                    <ClickToShowColumnOptionsWithToggleButtonGroup
                        columnMode={activeColumnMode}
                        setColumnMode={setActiveColumnMode}
                        gridColumnStateSessionVariableName={activeGridColumnStateSessionVar}
                        gridApi={gridApi}
                        disableMediumOption={true}
                        minColumnIds={activeMinColumnIds}
                        updateGridColumnModeFunction={activeUpdateColumnModeReactive}
                    />
                    <ClearRefresh
                        gridApi={gridApi}
                        showRefreshIcon={false}
                        refreshGridFunction={refreshGrid}
                        sseDataPullActive={sseDataPullActive}
                        showExcelExportIcon={true}
                        excelExportFunction={excelExport}
                    />
                </div>

                <div className="h-full flex flex-col gap-y-5" id="gridRoot">
                    {getGrid()}
                </div>
            </div>
        </div>
    )

    function getGrid() {
        return (
            <Grid
                columnDefs={showHourly ? hourlyColumnDefs : dailyColumnDefs}
                defaultColDef={defaultColDef}
                sideBar={sideBar}
                rowSelection={rowSelection}
                setGridApi={setGridApi}
                columnMode={activeColumnMode}
                setColumnMode={setActiveColumnMode}
                gridColumnStateSessionVariableName={activeGridColumnStateSessionVar}
                gridFilterStateSessionVariableName={activeGridFilterStateSessionVar}
                minColumnIds={activeMinColumnIds}
                updateColumnModeReactive={activeUpdateColumnModeReactive}
                updateFilterModelReactive={activeUpdateFilterModelReactive}
                updateColumnStateReactive={activeUpdateColumnStateReactive}
                showHourly={showHourly}
                sseDataPullActive={sseDataPullActive}
                setSSEDataPullActive={setSSEDataPullActive}
                asyncTransactionWaitMillis={asyncTransactionWaitMillis}
                setAsyncTransactionWaitMillis={setAsyncTransactionWaitMillis}
                excelExport={excelExport}
                setRawChartData={setRawChartData}
            />
        )
    }
}

// ─── Grid class ───────────────────────────────────────────────────────────────

class Grid extends Component {
    dailyRowData  = []
    hourlyRowData = []

    agentNamesListSessionStorage    = JSON.parse(decryptAndGetSessionVariable("agentNamesList"))
    dailyUpdateTransactionsToApply  = []
    hourlyUpdateTransactionsToApply = []
    dailyAbortController            = new AbortController()
    hourlyAbortController           = new AbortController()
    isSwappingView                  = false

    componentWillUnmount() {
        this.dailyAbortController.abort()
        this.hourlyAbortController.abort()
    }

    componentDidUpdate(prevProps) {
        // Swap visible dataset instantly on toggle — no remount, no reload
        if (prevProps.showHourly !== this.props.showHourly && this.gridApi && !this.gridApi.destroyCalled) {
            // Identify the outgoing view's session vars BEFORE anything changes
            const outgoingFilterVar = prevProps.showHourly
                ? hourlyGridFilterStateSessionVariableName
                : dailyGridFilterStateSessionVariableName
            const outgoingColumnVar = prevProps.showHourly
                ? hourlyGridColumnStateSessionVariableName
                : dailyGridColumnStateSessionVariableName

            // Step 1: Snapshot outgoing live filter + column state into session.
            // Must happen before setGridOption since that triggers onFilterChanged,
            // which by then sees the NEW props and would write into the wrong session var.
            const liveFilterModel = this.gridApi.getFilterModel()
            const liveColumnState = this.gridApi.getColumnState()

            try {
                const rawFilter = decryptAndGetSessionVariable(outgoingFilterVar)
                const parsedFilter = rawFilter ? JSON.parse(rawFilter) : {useFilterState: true}
                parsedFilter.filterModel = JSON.stringify(liveFilterModel)
                encryptAndStoreSessionVariable(outgoingFilterVar, JSON.stringify(parsedFilter))
            } catch (e) { console.log(e) }

            try {
                const rawCol = decryptAndGetSessionVariable(outgoingColumnVar)
                const parsedCol = rawCol ? JSON.parse(rawCol) : {useColumnState: true, columnMode: "custom"}
                parsedCol.columnStatesList = liveColumnState
                encryptAndStoreSessionVariable(outgoingColumnVar, JSON.stringify(parsedCol))
            } catch (e) { console.log(e) }

            // Step 2: Swap row data and restore incoming view's state.
            // We do NOT use isSwappingView to suppress onFilterChanged here —
            // instead we restore AFTER a setTimeout so AG Grid's full async pipeline
            // (row swap + any triggered filter re-evaluation) has fully flushed before
            // we write the incoming view's filter state.
            const data = this.props.showHourly ? this.hourlyRowData : this.dailyRowData
            this.gridApi.setGridOption("rowData", data)

            // Step 3: Clear the outgoing filter and restore the incoming view's state.
            // isSwappingView suppresses onFilterChanged for the clear + restore so
            // neither the null-clear nor the restore's setFilterModel call corrupts session.
            // setTimeout ensures AG Grid's row-swap microtasks have fully flushed first.
            const incomingFilterVar = this.props.gridFilterStateSessionVariableName
            const incomingColumnVar = this.props.gridColumnStateSessionVariableName
            const gridApi = this.gridApi
            // isSwappingView guards the null-clear only — we clear the flag before
            // calling onGridReadyHelper so the restore's onFilterChanged fires normally
            // and writes the incoming view's filter back into session correctly.
            this.isSwappingView = true
            gridApi.setFilterModel(null)
            setTimeout(() => {
                this.isSwappingView = false
                if (gridApi && !gridApi.destroyCalled) {
                    onGridReadyHelper({api: gridApi}, incomingFilterVar)
                    onGridReadyHelperForColumnState({api: gridApi}, incomingColumnVar)
                }
            }, 0)
        }
    }

    // Push both caches up to the page so the chart always has fresh data
    notifyChartData = () => {
        this.props.setRawChartData({
            daily:  [...this.dailyRowData],
            hourly: [...this.hourlyRowData],
        })
    }

    checkForAgentNameInSessionStorage = (rowData) => {
        if (!rowData.agentId || !this.agentNamesListSessionStorage) return
        for (let i = 0; i < this.agentNamesListSessionStorage.length; i++) {
            if (this.agentNamesListSessionStorage[i].agentId === rowData.agentId) {
                rowData.agentDisplayName = this.agentNamesListSessionStorage[i].name    || " "
                rowData.machineName      = this.agentNamesListSessionStorage[i].machine || " "
                break
            }
        }
    }

    populateDailyGrid = (rowData) => {
        if (!this.agentNamesListSessionStorage) {
            this.agentNamesListSessionStorage = JSON.parse(decryptAndGetSessionVariable("agentNamesList"))
        }
        this.checkForAgentNameInSessionStorage(rowData)

        const key      = `${rowData.zenGroupId}|${rowData.aiTool}|${rowData.username}|${rowData.dateDayRounded}`
        const existing = this.dailyRowData.find(
            r => `${r.zenGroupId}|${r.aiTool}|${r.username}|${r.dateDayRounded}` === key
        )
        if (existing) {
            existing.count = (existing.count || 0) + (rowData.count || 0)
            if (!this.props.showHourly && this.gridApi && !this.gridApi.destroyCalled) {
                this.gridApi.applyTransaction({update: [existing]})
            }
        } else {
            this.dailyRowData.push(rowData)
            if (!this.props.showHourly) {
                standardHandlePopulateGrid(rowData, this.gridApi)
            }
        }
        this.notifyChartData()
    }

    populateHourlyGrid = (rowData) => {
        if (!this.agentNamesListSessionStorage) {
            this.agentNamesListSessionStorage = JSON.parse(decryptAndGetSessionVariable("agentNamesList"))
        }
        this.checkForAgentNameInSessionStorage(rowData)

        const key      = `${rowData.zenGroupId}|${rowData.aiTool}|${rowData.username}|${rowData.dateHourRounded}`
        const existing = this.hourlyRowData.find(
            r => `${r.zenGroupId}|${r.aiTool}|${r.username}|${r.dateHourRounded}` === key
        )
        if (existing) {
            existing.count = (existing.count || 0) + (rowData.count || 0)
            if (this.props.showHourly && this.gridApi && !this.gridApi.destroyCalled) {
                this.gridApi.applyTransaction({update: [existing]})
            }
        } else {
            this.hourlyRowData.push(rowData)
            if (this.props.showHourly) {
                standardHandlePopulateGrid(rowData, this.gridApi)
            }
        }
        this.notifyChartData()
    }

    updateDailyGridForChangeStream = async (changeStreamData) => {
        const {operationType, body: obj} = changeStreamData
        if (operationType === "UPDATE" || operationType === "REPLACE") {
            const idx = this.dailyRowData.findIndex(r => r.id === obj.id)
            if (idx !== -1) this.dailyRowData[idx] = {...this.dailyRowData[idx], ...obj}
            if (!this.props.showHourly && !this.isSwappingView && !this.gridApi.destroyCalled) {
                const node = this.gridApi.getRowNode(obj.id)
                if (node?.data?.agentId === obj.agentId && node.data.agentDisplayName !== node.data.agentId) {
                    obj.agentDisplayName = node.data.agentDisplayName
                    obj.machineName      = node.data.machineName
                } else {
                    this.checkForAgentNameInSessionStorage(obj)
                }
                standardHandleUpdateAndReplaceEvent(obj, this.gridApi, this.props.sseDataPullActive, this.dailyUpdateTransactionsToApply)
            }
            this.notifyChartData()
        } else if (operationType === "INSERT") {
            this.checkForAgentNameInSessionStorage(obj)
            this.dailyRowData.push(obj)
            if (!this.props.showHourly && !this.isSwappingView) standardHandleInsertEvent(obj, this.gridApi, this.props.sseDataPullActive)
            this.notifyChartData()
        }
    }

    updateHourlyGridForChangeStream = async (changeStreamData) => {
        const {operationType, body: obj} = changeStreamData
        if (operationType === "UPDATE" || operationType === "REPLACE") {
            const idx = this.hourlyRowData.findIndex(r => r.id === obj.id)
            if (idx !== -1) this.hourlyRowData[idx] = {...this.hourlyRowData[idx], ...obj}
            if (this.props.showHourly && !this.isSwappingView && !this.gridApi.destroyCalled) {
                const node = this.gridApi.getRowNode(obj.id)
                if (node?.data?.agentId === obj.agentId && node.data.agentDisplayName !== node.data.agentId) {
                    obj.agentDisplayName = node.data.agentDisplayName
                    obj.machineName      = node.data.machineName
                } else {
                    this.checkForAgentNameInSessionStorage(obj)
                }
                standardHandleUpdateAndReplaceEvent(obj, this.gridApi, this.props.sseDataPullActive, this.hourlyUpdateTransactionsToApply)
            }
            this.notifyChartData()
        } else if (operationType === "INSERT") {
            this.checkForAgentNameInSessionStorage(obj)
            this.hourlyRowData.push(obj)
            if (this.props.showHourly && !this.isSwappingView) standardHandleInsertEvent(obj, this.gridApi, this.props.sseDataPullActive)
            this.notifyChartData()
        }
    }

    onColumnStateChanged = (params) => {
        const {gridColumnStateSessionVariableName, updateColumnModeReactive, updateColumnStateReactive, setColumnMode} = this.props
        if (params.source !== "api" && params.source !== "gridOptionsChanged") {
            setColumnMode && setColumnMode(customColumnModeText)
            onColumnStateChangedHelper(params, gridColumnStateSessionVariableName, updateColumnStateReactive)
            updateColumnModeInSessionHelper(gridColumnStateSessionVariableName, updateColumnModeReactive, customColumnModeText)
        } else if (params.source === "api" && params.type === "sortChanged") {
            setColumnMode && setColumnMode(customColumnModeText)
            onColumnStateChangedHelper(params, gridColumnStateSessionVariableName, updateColumnStateReactive)
            updateColumnModeInSessionHelper(gridColumnStateSessionVariableName, updateColumnModeReactive, customColumnModeText)
        }
    }

    getContextMenuItems = () => ["resetColumns", "autoSizeAll"]

    cellEditingStoppedFunctionCaller = (event) => {
        if (event.column.colId === "zenGroupDisplayName") {
            handleGroupColumnChangeNameOnly(event)
        } else if (event.column.colId === "agentDisplayName") {
            const api    = event.api
            const newVal = event.newValue?.trim()
            if (!newVal || newVal === event.oldValue) {
                event.data.agentDisplayName = event.oldValue
                api.refreshCells({suppressFlash: true})
                return
            }
            if (!event.data.agentId || !event.data.zenGroupId) {
                NotificationManager.error("Missing agent or group data.")
                event.data.agentDisplayName = event.oldValue
                api.refreshCells({suppressFlash: true})
                return
            }
            changeAgentNameReactive(newVal, event.data.zenGroupId, event.data.agentId)
                .then(() => {
                    NotificationManager.success("Successfully changed this agent's name")
                    event.node.setDataValue("agentDisplayName", newVal)
                })
                .catch((error) => {
                    NotificationManager.error(error.message || "Error changing agent name")
                    event.data.agentDisplayName = event.oldValue
                    api.refreshCells({suppressFlash: true})
                })
        }
    }

    onGridReady = async (params) => {
        ["keyup", "keydown"].forEach(event => {
            window.addEventListener(event, (e) => {
                document.onselectstart = () => !(e.shiftKey || e.ctrlKey)
            })
        })

        this.gridApi = params.api
        this.props.setGridApi(params.api)
        params.api.hideOverlay()

        const {
            columnMode, setColumnMode,
            gridColumnStateSessionVariableName,
            gridFilterStateSessionVariableName,
            minColumnIds, updateColumnModeReactive,
        } = this.props

        if (columnMode === customColumnModeText) {
            onGridReadyHelperForColumnState(params, gridColumnStateSessionVariableName)
        } else if (columnMode === minColumnModeText) {
            standardApplyMinimumOrMediumColumnMode(
                gridColumnStateSessionVariableName, params.api, setColumnMode,
                minColumnModeText, minColumnIds, updateColumnModeReactive
            )
        }

        params.api.applyColumnState({state: [{colId: "count", pinned: "left"}]})
        onGridReadyHelper(params, gridFilterStateSessionVariableName)

        // Load both views in parallel — grid shows active view, both caches feed the chart
        loadDataWithSSEAndStartChangeStreamListener(
            "/sse/fortressAiReportMetrics24hrListReactive",
            "/sse/listenToFortressAiReportMetricsEvent",
            this.populateDailyGrid,
            this.updateDailyGridForChangeStream,
            params,
            this.props.setSSEDataPullActive,
            this.props.setAsyncTransactionWaitMillis,
            this.dailyUpdateTransactionsToApply,
            this.dailyAbortController
        )

        loadDataWithSSEAndStartChangeStreamListener(
            "/sse/fortressAiReportMetricsHourlyListReactive",
            "/sse/listenToFortressAiReportMetricsHourlyEvent",
            this.populateHourlyGrid,
            this.updateHourlyGridForChangeStream,
            params,
            this.props.setSSEDataPullActive,
            this.props.setAsyncTransactionWaitMillis,
            this.hourlyUpdateTransactionsToApply,
            this.hourlyAbortController
        )
    }

    render() {
        return (
            <div className={"w-full h-full"} style={{minHeight: "400px"}}>
                <div id="metricsGrid" className="ag-theme-alpine rounded-md shadow h-full w-full">
                    <AgGridReact
                        columnMenu={"legacy"}
                        modules={[
                            AllCommunityModule,
                            ClientSideRowModelModule,
                            ColumnMenuModule,
                            ContextMenuModule,
                            ColumnsToolPanelModule,
                            SetFilterModule,
                            MultiFilterModule,
                            ExcelExportModule,
                            CsvExportModule,
                        ]}
                        defaultColDef={this.props.defaultColDef}
                        columnDefs={this.props.columnDefs}
                        components={{agDateInput: DTPicker, customNameCellEditor: CustomNameCellEditor}}
                        asyncTransactionWaitMillis={this.props.asyncTransactionWaitMillis}
                        suppressModelUpdateAfterUpdateTransaction={true}
                        getRowId={(params) => params.data.id}
                        suppressOverlays={["noRows"]}
                        suppressCsvExport={true}
                        suppressMultiSort={true}
                        maintainColumnOrder={true}
                        onGridReady={this.onGridReady}
                        onCellEditingStopped={this.cellEditingStoppedFunctionCaller}
                        rowSelection={this.props.rowSelection}
                        enableCellTextSelection={true}
                        ensureDomOrder={true}
                        onFilterChanged={(params) => {
                            if (this.isSwappingView) return
                            onFilterChangedHelper(
                                params,
                                this.props.gridFilterStateSessionVariableName,
                                this.props.updateFilterModelReactive
                            )
                        }}
                        onSortChanged={this.onColumnStateChanged}
                        onColumnMoved={this.onColumnStateChanged}
                        onColumnVisible={this.onColumnStateChanged}
                        getContextMenuItems={this.getContextMenuItems}
                        sideBar={this.props.sideBar}
                    />
                </div>
            </div>
        )
    }
}